import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, HardDrive, RefreshCw, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import PricingTable from '../components/PricingTable';
import DualPipelineFlow from '../components/DualPipelineFlow';
import StudioBrandShowcase from '../components/StudioBrandShowcase';

const LIVE_PIPELINE = [
    {
        icon: HardDrive,
        title: 'Heavy Media Support',
        subtitle: 'Built for 4K ProRes & RAW files.',
        tooltip: 'Traditional drives choke or freeze on massive video files. Magnidrop supports uncompressed video uploads up to 5 TB with zero crashes.',
    },
    {
        icon: Wifi,
        title: '4x Parallel Uploads',
        subtitle: 'Max out your network speeds.',
        tooltip: 'Generic cloud storage solutions artificially throttle your upload bandwidth. We use parallel data lanes to push your video exports at the maximum physical speed of your internet.',
    },
    {
        icon: RefreshCw,
        title: 'Resume-on-Disconnect',
        subtitle: 'Fail-proof render transits.',
        tooltip: 'Tired of an upload failing at 99% and completely restarting from scratch? If your network drops mid-export, our system automatically pauses and resumes exactly where it left off.',
    },
    {
        icon: Shield,
        title: '7-Day Auto-Purge',
        subtitle: 'Self-clearing storage safety net.',
        tooltip: 'Stop wasting time manually scouring old folders just to clear up space for a new project. Your storage clears itself out on autopilot 7 days after delivery.',
    },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 opacity-30">
                    <img
                        src="https://images.pexels.com/photos/12810665/pexels-photo-12810665.jpeg"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-background/60" />
                </div>
                <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8 stagger">
                        <span className="inline-flex items-center gap-2 border border-[#FF6B35]/40 text-[#FF6B35] px-3 py-1 text-xs uppercase tracking-[0.2em]">
                            <Zap className="h-3 w-3" /> High-speed transit pipe
                        </span>
                        <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05]">
                            Send & receive up to{' '}
                            <span className="text-[#FF6B35]">5 TB files</span>{' '}
                            at the maximum{' '}
                            physical speed of your internet.
                        </h1>
                        <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                            Zero-friction pipelines for creators, video teams and remote media agencies. Clients drop files — no logins, no sign-ups. You get them, fast.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/login">
                                <Button data-testid="hero-start-btn" size="lg" className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">
                                    Start free <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/pricing">
                                <Button data-testid="hero-pricing-btn" size="lg" variant="outline">See pricing</Button>
                            </Link>
                        </div>
                    </div>
                    <div className="hidden lg:block lg:col-span-4">
                        <div className="border border-border bg-card p-8" data-testid="live-pipeline">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Live pipeline</p>
                            <div className="mt-6 space-y-4">
                                {LIVE_PIPELINE.map(({ icon: Icon, title, subtitle, tooltip }) => (
                                    <Tooltip key={title}>
                                        <TooltipTrigger asChild>
                                            <div
                                                data-testid={`pipeline-item-${title.toLowerCase().replace(/\s+/g, '-')}`}
                                                className="flex items-center gap-3 p-2 -mx-2 rounded-sm cursor-help transition-colors hover:bg-[#FF6B35]/5"
                                            >
                                                <div className="h-9 w-9 border border-border grid place-items-center shrink-0">
                                                    <Icon className="h-4 w-4 text-[#FF6B35]" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate">{title}</p>
                                                    <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
                                                </div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="max-w-xs leading-relaxed">
                                            {tooltip}
                                        </TooltipContent>
                                    </Tooltip>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dual-pipeline flowchart — directly below hero, above feature grid */}
            <DualPipelineFlow />

            {/* Studio identity showcase — Your Pipeline. Your Brand. */}
            <StudioBrandShowcase />

            {/* USP strip */}
            <section className="border-b border-border py-16 bg-card/50">
                <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-3 gap-8">
                    {[
                        { title: 'Streaming off disk', body: 'Web Streams API parses and uploads files chunk-by-chunk directly off physical disk. Zero RAM crashes, zero tab freezes.' },
                        { title: 'Drop-resilient', body: 'IndexedDB tracks every completed chunk. Lost connection? Uploads pause and auto-resume the moment you\'re back online.' },
                        { title: 'Predictable pricing', body: 'Cloudflare R2 backbone with $0 egress means flat operational cost — no surprise bandwidth bills, ever.' },
                    ].map((f) => (
                        <div key={f.title} className="p-6 border border-border bg-background">
                            <h3 className="font-heading text-2xl font-medium">{f.title}</h3>
                            <p className="mt-3 text-muted-foreground leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="border-b border-border py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pricing</p>
                        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter">Simple tiers, predictable costs.</h2>
                        <p className="mt-4 text-muted-foreground">Four plans mapped to real production workflows. Cancel anytime.</p>
                    </div>
                    <div className="mt-12">
                        <PricingTable onSelect={() => (window.location.href = '/login')} />
                    </div>
                </div>
            </section>

            {/* Target audience */}
            <section className="py-24">
                <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <img
                            src="https://images.pexels.com/photos/11063289/pexels-photo-11063289.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt="Media workstation"
                            className="w-full aspect-[4/3] object-cover"
                        />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Built for</p>
                        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter">Remote media agencies. Video teams. Post-production houses.</h2>
                        <p className="mt-6 text-muted-foreground leading-relaxed">
                            Stop chasing WeTransfer expirations and dropbox invite links. Share one Magnidrop portal URL — your client drops raw footage, you get a Slack ping when it lands.
                        </p>
                        <div className="mt-8 flex gap-4">
                            <Link to="/login"><Button data-testid="cta-signup-btn" className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">Get your portal URL</Button></Link>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-border py-10">
                <div className="mx-auto max-w-7xl px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
                    <p>© {new Date().getFullYear()} Magnidrop. Built for high-video-consuming teams.</p>
                    <div className="flex gap-6">
                        <Link to="/pricing" className="hover:text-[#FF6B35]">Pricing</Link>
                        <Link to="/login" className="hover:text-[#FF6B35]">Sign in</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
